import { EmptyState } from './EmptyState.js';
import { useState, useEffect } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import type { DashboardOverview, DashboardEnginesResponse, DashboardSourcesResponse, DashboardEngineItem, DashboardSourceItem } from '../types/index.js';
import { Icon, Spinner } from './ui/Icon.js';

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-50 text-violet-600">{icon}</div>
        <div>
          <p className="hub-stat__value">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export function TenantOverview() {
  const { user, activeTenantId, bootstrapTenant } = useBrand();
  const [overview, setOverview] = useState<DashboardOverview['data'] | null>(null);
  const [engines, setEngines] = useState<DashboardEngineItem[]>([]);
  const [sources, setSources] = useState<DashboardSourceItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Spec 076: bootstrap a tenant id if there is no selected brand yet.
  useEffect(() => {
    if (activeTenantId) return;
    bootstrapTenant().catch(() => {
      // Non-fatal — overview simply renders empty state.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  useEffect(() => {
    if (!user || !activeTenantId) return;
    let cancelled = false;
    const tid = activeTenantId;
    async function load() {
      try {
        const [ov, eng, src] = await Promise.all([
          apiGet<DashboardOverview>(`/api/v1/dashboard/overview?tenant_id=${tid}`),
          apiGet<DashboardEnginesResponse>(`/api/v1/dashboard/engines?tenant_id=${tid}`),
          apiGet<DashboardSourcesResponse>(`/api/v1/dashboard/sources?tenant_id=${tid}`),
        ]);
        if (!cancelled) {
          setOverview(ov.data);
          setEngines(eng.data);
          setSources(src.data);
        }
      } catch {
        // ignore — graceful degradation
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, activeTenantId]);

  if (loading) {
    return (
      <div className="mt-8 flex justify-center">
        <Spinner className="text-violet-600" />
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="mt-8"><EmptyState title="No data available" description="Select a brand or configure queries to get started." /></div>
    );
  }

  return (
    <>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="overview-cards">
        <StatCard label="Brands" value={overview.total_brands} icon={<Icon name="bx-bar-chart-alt-2" />} />
        <StatCard label="Queries (24h)" value={overview.total_queries_24h} icon={<Icon name="bx-bolt-circle" />} />
        <StatCard label="Total responses" value={overview.total_responses} icon={<Icon name="bx-pulse" />} />
        <StatCard label="Active playbooks" value={overview.active_playbooks} icon={<Icon name="bx-bar-chart-alt-2" />} />
        <StatCard label="Gaps detected" value={overview.total_gaps} icon={<Icon name="bx-globe" />} />
      </div>

      {/* Engines Table */}
      {engines.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-slate-900">Engine performance</h2>
          {/* Spec 232 — no rounded-and-bordered wrapper: this component renders
              on a dashboard route, and gate 231.A.2 forbids exactly that shape. */}
          <div className="mt-3 overflow-x-auto">
            <table className="hub-table" data-testid="engine-table">
              <thead>
                <tr>
                  <th>Engine</th>
                  <th className="is-num">Total</th>
                  <th className="is-num">Completed</th>
                  <th className="is-num">Failed</th>
                  <th className="is-num">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {engines.map((e) => (
                  <tr key={e.engine}>
                    <td className="font-medium">{e.engine}</td>
                    <td className="is-num">{e.total_queries}</td>
                    <td className="is-num text-[var(--hub-color-success)]">{e.completed}</td>
                    <td className="is-num text-[var(--hub-color-danger)]">{e.failed}</td>
                    <td className="is-num">{e.avg_latency_ms ?? '--'}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Sources */}
      {sources.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-slate-900">Top sources</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="hub-table" data-testid="sources-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Type</th>
                  <th className="is-num">Citations</th>
                  <th className="is-num">Cited</th>
                  <th className="is-num">Retrieved</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.domain}>
                    <td className="font-medium">{s.display_name ?? s.domain}</td>
                    <td className="text-[var(--hub-color-ink-muted)]">{s.type}</td>
                    <td className="is-num">{s.citation_count}</td>
                    <td className="is-num">{s.cited_count}</td>
                    <td className="is-num">{s.retrieved_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
